/**
 * Website Home / LiB shared loading placeholders — template pulse bones
 * (`mte-tpl-skel-*` / `mte-wtgl`), not classic `react-loading-skeleton`.
 */
import React from "react";
import DataSkeleton from "../../../components/StayUpToDateComponent/UpToDateComponents/DataSkeleton";
import {
  FeaturedCarouselStripSkeleton,
  featuredCarouselStripIsProductish,
} from "./FeaturedCarouselStripSkeleton";
import { TemplateSectionGridLoader } from "./TemplateSectionGridLoader";
import { useWebsiteLibEditAsDrawer } from "../../../components/EditModalComponents.tsx/WebsiteLibEditAsDrawerContext";
import { useWebsiteTemplateLoaders } from "./TemplateFeedDesignBoundary";

export function WebsiteLibLandingSkeleton({
  isDetailView = false,
  blockArray = 3,
  device = "desktop",
  product = false,
  contact = false,
}: {
  isDetailView?: boolean;
  blockArray?: number;
  device?: "desktop" | "tablet" | "mobile";
  product?: boolean;
  contact?: boolean;
}) {
  if (contact) {
    return (
      <div
        className="mte-tpl-cf-form mte-tpl-cf-form--single"
        role="status"
        aria-label="Loading contact form"
      >
        <div className="mte-tpl-cf-field-label mte-tpl-cf-field-label--full">
          <div className="mte-tpl-cf-skel-line" style={{ width: 180, height: 18 }} />
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="mte-tpl-cf-field-label mte-tpl-cf-field-label--full">
            <div
              className="mte-tpl-cf-skel-line"
              style={{ width: 56, height: 9, animationDelay: `${i * 80}ms` }}
            />
            <div
              className="mte-tpl-cf-skel-field"
              style={{ animationDelay: `${40 + i * 80}ms` }}
            />
          </div>
        ))}
        <div className="mte-tpl-cf-field-label mte-tpl-cf-field-label--full">
          <div className="mte-tpl-cf-skel-btn" style={{ animationDelay: "200ms" }} />
        </div>
      </div>
    );
  }

  if (isDetailView) {
    return (
      <TemplateSectionGridLoader
        variant="content"
        device={device}
        tileLayout="feed"
        count={device === "mobile" ? 4 : 10}
      />
    );
  }

  const strips = Math.max(1, Number(blockArray) || 3);
  return (
    <div className="mte-lib-feed-skel" role="status" aria-label="Loading">
      {Array.from({ length: strips }, (_, i) => (
        <div key={i} className="mte-lib-feed-skel__block">
          <div className="mte-tpl-skel-line mte-tpl-skel-line--md mte-lib-feed-skel__title" />
          <FeaturedCarouselStripSkeleton product={product && i === 0} />
        </div>
      ))}
    </div>
  );
}

function componentIsProductish(component: any): boolean {
  if (!component) return false;
  return featuredCarouselStripIsProductish({
    type: component?.type === "shop" ? "shop" : component?.type,
    componentType: String(
      component?.display_name || component?.type || "",
    ),
  } as any);
}

export type StayFeedLoadingPlaceholderProps = {
  from?: string;
  /** When set on shop/content tabs, parent already paints the website skeleton. */
  templateGridLoaderProps?: {
    variant?: "shop" | "content";
    device?: "desktop" | "tablet" | "mobile";
    columns?: number;
  } | null;
  /** Explicit override when not under TemplateFeedDesignBoundary. */
  useWebsiteLoaders?: boolean;
  individualGrid?: boolean;
  expandedSlot?: boolean;
  arrayLength?: number;
  component?: any;
  designTokens?: any;
  dashboard?: boolean;
  [key: string]: any;
};

/**
 * StayUpToDate feed loading UI:
 * - Public pages skip the skeleton when the row is already `isEmpty`
 *   (that section has no posts and is not shown). Feeds that render still
 *   show a loader while they load.
 * - Legacy dashboard (`from="preview"`) and website LiB editor always show it.
 * - `website-template` shop/content tabs → null (parent already shows bones)
 */
export function StayFeedLoadingPlaceholder({
  from,
  templateGridLoaderProps,
  useWebsiteLoaders = false,
  individualGrid,
  expandedSlot,
  arrayLength,
  component,
  designTokens,
  dashboard,
  ...rest
}: StayFeedLoadingPlaceholderProps) {
  const inWebsiteSurface = useWebsiteTemplateLoaders();
  const inLibEditor = useWebsiteLibEditAsDrawer();

  if (from === "website-template") {
    return null;
  }

  const editorPreview =
    from === "preview" || Boolean(dashboard) || inLibEditor;
  const markedEmpty =
    component?.isEmpty === true || component?.isEmpty === "true";
  if (!editorPreview && markedEmpty) {
    return null;
  }

  const website =
    useWebsiteLoaders || inWebsiteSurface || Boolean(templateGridLoaderProps);

  if (!website) {
    return (
      <DataSkeleton
        arrayLength={arrayLength}
        individualGrid={individualGrid}
        expandedSlot={expandedSlot}
        from={from}
        component={component}
        designTokens={designTokens}
        dashboard={dashboard}
        {...rest}
      />
    );
  }

  const device: "desktop" | "tablet" | "mobile" =
    templateGridLoaderProps?.device ||
    (typeof window !== "undefined" && window.innerWidth < 890
      ? "mobile"
      : "desktop");

  if (individualGrid && !expandedSlot && !inLibEditor) {
    return (
      <TemplateSectionGridLoader
        variant={templateGridLoaderProps?.variant || "content"}
        device={device}
        columns={templateGridLoaderProps?.columns}
        tileLayout="feed"
        count={
          typeof arrayLength === "number" && arrayLength > 0
            ? arrayLength
            : device === "mobile"
              ? 4
              : 10
        }
      />
    );
  }

  return (
    <FeaturedCarouselStripSkeleton
      product={!inLibEditor && componentIsProductish(component)}
      square={
        inLibEditor && String(component?.content_shape || "") === "sq"
      }
      count={
        typeof arrayLength === "number" && arrayLength > 0
          ? Math.min(arrayLength, 8)
          : 5
      }
    />
  );
}
